import { WlWindow } from 'typings/globals/cordova';
import { isCordova } from 'utils/platform';
import useAuthStore from '~/authentication/store';

import { BusinessEvent, BusinessEventHandler } from '../../businessLayer';

declare const window: WlWindow;

const ZENDESK_KEYS = {
  host: 'c6b071ab-6138-44a4-8f51-99b2ffbedd7e',
  guest: '96f2ac89-1e8a-46d2-a3ae-52a697c6339e',
} as const;

const zendeskState: {
  loaded: boolean;
  key?: string;
  waitingForHost?: boolean;
  unsubscribeAuth?: () => void;
} = {
  loaded: false,
  key: undefined,
  waitingForHost: false,
  unsubscribeAuth: undefined,
};

function injectZendeskSnippet(key: string) {
  if (zendeskState.loaded) {
    return;
  }

  if (document.getElementById('ze-snippet') || (window as any)?.zE) {
    zendeskState.loaded = true;
    zendeskState.key = key;
    return;
  }

  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.async = true;
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
  script.onload = () => {
    console.log('[Zendesk] snippet loaded for key', key);
  };
  script.onerror = () => {
    console.warn('[Zendesk] failed to load snippet for key', key);
  };
  document.head.appendChild(script);

  zendeskState.loaded = true;
  zendeskState.key = key;
}

type Auth = {
  realAccount?: { is_host?: boolean } | null;
  account?: { is_host?: boolean } | null;
  user?: unknown;
  isHost?: () => boolean | undefined;
};

function resolveIsHostFromRealAccount(
  auth: Auth | undefined,
): boolean | undefined {
  return typeof auth?.realAccount?.is_host === 'boolean'
    ? auth?.realAccount?.is_host
    : undefined;
}

function resolveIsHostFromAccount(auth: Auth | undefined): boolean | undefined {
  return typeof auth?.account?.is_host === 'boolean'
    ? auth?.account?.is_host
    : undefined;
}

function resolveIsHostFromRole(auth: Auth | undefined): boolean | undefined {
  if (auth?.user && typeof auth.isHost === 'function') {
    const roleIsHost = auth.isHost();
    if (typeof roleIsHost === 'boolean') return roleIsHost;
  }
  return undefined;
}

function tryResolveHostFromAuthStore(): boolean | undefined {
  try {
    const auth = useAuthStore.getState?.();
    return (
      resolveIsHostFromRealAccount(auth) ??
      resolveIsHostFromAccount(auth) ??
      resolveIsHostFromRole(auth)
    );
  } catch (e) {
    console.warn('[Zendesk] tryResolveHostFromAuthStore failed', e);
    return undefined;
  }
}

function startAuthStoreWatcher() {
  if (zendeskState.waitingForHost || zendeskState.loaded) return;
  zendeskState.waitingForHost = true;
  try {
    zendeskState.unsubscribeAuth = useAuthStore.subscribe(
      (state) => ({
        realAccount: state.realAccount,
        account: state.account,
        user: state.user,
      }),
      () => {
        const resolved = tryResolveHostFromAuthStore();
        if (typeof resolved === 'boolean' && !zendeskState.loaded) {
          const key = resolved ? ZENDESK_KEYS.host : ZENDESK_KEYS.guest;
          injectZendeskSnippet(key);
          zendeskState.unsubscribeAuth?.();
          zendeskState.unsubscribeAuth = undefined;
          zendeskState.waitingForHost = false;
        }
      },
    );
    const initial = tryResolveHostFromAuthStore();
    if (typeof initial === 'boolean' && !zendeskState.loaded) {
      const key = initial ? ZENDESK_KEYS.host : ZENDESK_KEYS.guest;
      injectZendeskSnippet(key);
      zendeskState.unsubscribeAuth?.();
      zendeskState.unsubscribeAuth = undefined;
      zendeskState.waitingForHost = false;
    }
  } catch (e) {
    console.warn('[Zendesk] failed to subscribe to auth store', e);
    zendeskState.waitingForHost = false;
  }
}

const zendeskHandler: BusinessEventHandler = (evt: BusinessEvent) => {
  if (isCordova()) {
    // Do not load in Cordova
    return;
  }

  if (zendeskState.loaded) {
    // Already loaded
    return;
  }

  let isHost = evt.account_is_host as boolean | undefined;

  // Fallback 1: read from auth store
  if (typeof isHost !== 'boolean') {
    const resolved = tryResolveHostFromAuthStore();
    if (typeof resolved === 'boolean') {
      isHost = resolved;
    }
  }

  // Fallback 2: infer from URL path
  if (typeof isHost !== 'boolean') {
    const path = window?.location?.pathname || '';
    if (/^\/?host\b|\/host\//.test(path)) {
      isHost = true;
    }
  }

  if (typeof isHost !== 'boolean') {
    startAuthStoreWatcher();
    return;
  }

  const key = isHost ? ZENDESK_KEYS.host : ZENDESK_KEYS.guest;
  injectZendeskSnippet(key);
};

export { zendeskHandler };
